import React, { type JSX } from 'react';
import { clone, equals } from 'ramda';
import mergeSort from '../../../Utils/mergeSort';
import {
  ISortInfo,
  createSortHandler,
  ISortProperty,
} from './SortableTableUtils';

interface IProps<T> {
  sortInfo: ISortInfo<T>;
  /** Function that compares two values to eachother. */
  compareFunction: (
    a: string | number,
    b: string | number,
    order: ISortOrder
  ) => number;
  /** From a row and an column name, get the value that should be used for comparison */
  getValue: (row: T, columnName: string) => string | number | null;
  children: (args: IChildrenArgs<T>) => JSX.Element;
}

export type ISortOrder = 'asc' | 'desc' | undefined;

interface IState<T> {
  sortInfo: ISortInfo<T>;
  prevSortInfo: ISortInfo<T> | null;
}

interface IChildrenArgs<T> {
  sortInfo: ISortInfo<T>;
  createSortHandler: (orderBy: string, caption: string) => () => void;
}

class EDSortableTable<T> extends React.Component<IProps<T>, IState<T>> {
  constructor(props: IProps<T>) {
    super(props);

    this.state = {
      sortInfo: this.sortRows(props.sortInfo),
      prevSortInfo: null,
    };
  }

  componentDidUpdate(prevProps: IProps<T>) {
    if (!equals<T[]>(prevProps.sortInfo.rows, this.props.sortInfo.rows)) {
      this.setSortInfoRow(this.props.sortInfo.rows);
    }
  }

  setSortInfo = (info: ISortInfo<T>) => {
    this.setState({
      sortInfo: this.sortRows({ ...info, rows: this.props.sortInfo.rows }),
      prevSortInfo: info.properties.length === 1 ? { ...info } : null,
    });
  };

  setSortInfoRow = (rows: T[]) => {
    this.setState(prevState => ({
      sortInfo: {
        ...prevState.sortInfo,
        rows: this.sortRows({
          ...(prevState.prevSortInfo ?? prevState.sortInfo),
          rows,
        }).rows,
      },
    }));
  };

  sortRows = (sortInfo: ISortInfo<T>): ISortInfo<T> => {
    if (sortInfo.properties.length > 0) {
      sortInfo.rows = mergeSort(sortInfo.rows, (a, b) =>
        this.compare(a, b, clone(sortInfo.properties))
      );
    }

    return sortInfo;
  };

  compare = (a: T, b: T, sortProperties: ISortProperty[]): number => {
    if (sortProperties.length === 0) {
      return 0;
    }
    const property = sortProperties[0];
    const aCell = this.props.getValue(a, property.orderBy);
    const bCell = this.props.getValue(b, property.orderBy);
    if (aCell == null && bCell == null) {
      return 0;
    }
    if (aCell == null) {
      return 1;
    }
    if (bCell == null) {
      return -1;
    }
    const trimmedProperties =
      sortProperties.length > 1 ? sortProperties.slice(1) : [];
    if (this.cellIsString(aCell) || this.cellIsString(bCell)) {
      const comparison = this.props.compareFunction(
        aCell,
        bCell,
        property.order
      );

      return comparison === 0
        ? this.compare(a, b, trimmedProperties)
        : comparison;
    }

    const comparison = this.props.compareFunction(
      Number.parseFloat(aCell as string),
      Number.parseFloat(bCell as string),
      property.order
    );

    return comparison === 0
      ? this.compare(a, b, trimmedProperties)
      : comparison;
  };

  cellIsString = (cell: number | string) => {
    return cell === '' || Number.isNaN(+cell);
  };

  nullFunction = () => {};

  render() {
    return this.props.children({
      sortInfo: this.state.sortInfo,
      createSortHandler: createSortHandler(
        this.state.sortInfo,
        this.setSortInfo
      ),
    });
  }
}

export default EDSortableTable;
